// FLOW-06 (SPEC S4; PLAN G15, G24, G28; Tasks 6.6 e 6.7) — as execuções da conta ao vivo, fora das
// páginas, no molde da sessão de teste do editor (`sessaoDeTeste.js`).
//
// O PowerTools manda à plataforma, em lote, a mudança de estado e o fim de cada passo das execuções
// duráveis, e o Rails transmite o lote como veio no stream da conta (`PowerflowExecucoesChannel`): `{event:
// 'powerflow_execucoes.eventos', data: {account_id, eventos}}`. A assinatura vai pela MESMA conexão do
// painel (`helper/conexaoDoCabo.js`), com a autenticação do `RoomChannel`.
//
// Os eventos (o contrato entre as trilhas, G28):
// - `{tipo: 'execucao.estado', execucao_id, em, ...campos do item da lista}`: a execução mudou de estado;
// - `{tipo: 'execucao.passo', execucao_id, em, passo: {caminho, tipo, estado, geracao, grava,
//   iniciado_em, terminado_em, erro?}}`: um passo começou ou terminou. O passo vem aninhado (o `tipo`
//   do evento e o do passo colidiam) e sem `saida`: quem quer a saída relê o detalhe.
//
// No agente (o PowerAgents ou o agent-runner), o PowerTools grava só `quem: {agent_ref}`, e o Rails
// completa o nome do PowerAgent na lista e no detalhe, mas não no canal (G24): o evento não apaga o nome
// que a tela já tem, e a linha nova do agente sem nome é relida pelo detalhe (`aCompletar`).
import { assinarCanal } from 'dashboard/helper/conexaoDoCabo';
import { doAgente } from './useExecucaoNaTela';

export const EVENTO_DO_CABO = 'powerflow_execucoes.eventos';

// A execução que ainda pode mudar.
export const VIVAS = ['enfileirada', 'rodando'];

// Assina o stream da conta. `aoReceber(eventos)` recebe só os lotes desta conta; `aoConectar` vem a cada
// (re)conexão (o que passou com o cabo fora se busca por HTTP); `aoRecusar`, quando o servidor recusa.
// Devolve a assinatura (com `unsubscribe`), ou nulo sem conexão aberta.
export function assinarExecucoes(
  accountId,
  { aoReceber, aoConectar, aoRecusar }
) {
  return assinarCanal(
    { channel: 'PowerflowExecucoesChannel', account_id: accountId },
    {
      received: dados => {
        if (dados?.event !== EVENTO_DO_CABO) return;
        if (Number(dados.data?.account_id) !== Number(accountId)) return;
        const eventos = Array.isArray(dados.data?.eventos)
          ? dados.data.eventos
          : [];
        if (eventos.length) aoReceber(eventos);
      },
      connected: () => aoConectar?.(),
      rejected: () => aoRecusar?.(),
    }
  );
}

// Os campos do item que um `execucao.estado` traz, sem o tipo e o instante do evento.
const camposDoItem = ({ tipo, em, ...campos }) => campos;

// O que chegou por cima do que a tela tem. `quem` se junta campo a campo, para o `agent_ref` do evento
// não apagar o nome que o Rails completou.
export function juntarExecucao(atual, novo) {
  const juntos = { ...atual, ...novo };
  if (atual?.quem && novo?.quem) juntos.quem = { ...atual.quem, ...novo.quem };
  return juntos;
}

const instante = valor => Date.parse(valor);

// Se a execução entra na lista com estes filtros (os do pedido ao servidor, `pedidoDosFiltros`): o
// período vale pela criação, e a busca pelo id, inteira.
export function casaComOsFiltros(item, pedido = {}) {
  const igual = (filtro, valor) =>
    filtro === undefined || String(filtro) === String(valor ?? '');
  if (!igual(pedido.estado, item.estado)) return false;
  if (!igual(pedido.flow_nome, item.flow?.nome)) return false;
  if (!igual(pedido.entrada_id, item.flow?.entrada_id)) return false;
  if (!igual(pedido.invocador, item.invocador)) return false;
  if (!igual(pedido.conversation_id, item.conversation_id)) return false;
  if (!igual(pedido.execucao_id, item.execucao_id)) return false;
  const criada = instante(item.criada_em);
  if (pedido.de && !(criada >= instante(pedido.de))) return false;
  if (pedido.ate && !(criada <= instante(pedido.ate))) return false;
  return true;
}

const doAgenteSemNome = item => doAgente(item) && !item.quem?.nome;
const maisNovaPrimeiro = (a, b) =>
  (instante(b.criada_em) || 0) - (instante(a.criada_em) || 0);

// A lista de Execuções com os eventos aplicados. A execução que a lista tem muda no lugar; a que ela
// não tem entra no topo quando vem com o item (o `flow`) e casa com os filtros. `aCompletar` são as
// novas do agente que chegaram sem o nome dele. O evento de passo não muda a lista.
export function aplicarNaLista(execucoes, eventos, pedido) {
  const lista = [...execucoes];
  const novas = [];
  eventos
    .filter(evento => evento?.tipo === 'execucao.estado' && evento.execucao_id)
    .forEach(evento => {
      const campos = camposDoItem(evento);
      const mesma = item => item.execucao_id === evento.execucao_id;
      const naLista = lista.findIndex(mesma);
      if (naLista >= 0) {
        lista[naLista] = juntarExecucao(lista[naLista], campos);
        return;
      }
      const entre = novas.findIndex(mesma);
      if (entre >= 0) {
        novas[entre] = juntarExecucao(novas[entre], campos);
        return;
      }
      if (campos.flow && casaComOsFiltros(campos, pedido)) novas.push(campos);
    });
  novas.sort(maisNovaPrimeiro);
  return {
    execucoes: [...novas, ...lista],
    aCompletar: novas.filter(doAgenteSemNome).map(item => item.execucao_id),
  };
}

// A primeira página relida (a reconexão, a sondagem sem cabo) por cima da lista: ela vale como veio do
// servidor, e as que a tela tinha depois dela (as páginas mais antigas já carregadas) ficam. A que
// sumiu do meio da página não casa mais com os filtros, e sai.
export function mesclarPagina(execucoes, pagina) {
  if (!pagina.length) return [...execucoes];
  const ids = new Set(pagina.map(item => item.execucao_id));
  const ultima = instante(pagina[pagina.length - 1].criada_em);
  const depois = execucoes.filter(
    item => !ids.has(item.execucao_id) && instante(item.criada_em) < ultima
  );
  return [...pagina, ...depois];
}

// O detalhe de uma execução com os eventos dela aplicados: o estado nos campos do item, e o passo no
// lugar dele (pelo `caminho`), ou no fim. `reler` diz que a tela precisa do detalhe do servidor: o passo
// que terminou tem saída guardada que o evento não traz (G28), e a execução que terminou traz a
// resposta e o que o reexecutar precisa (G25).
export function aplicarNoDetalhe(detalhe, eventos) {
  let atual = detalhe;
  let reler = false;
  eventos
    .filter(evento => evento?.execucao_id === detalhe.execucao_id)
    .forEach(evento => {
      if (evento.tipo === 'execucao.estado') {
        atual = juntarExecucao(atual, camposDoItem(evento));
        if (!VIVAS.includes(atual.estado)) reler = true;
        return;
      }
      if (evento.tipo !== 'execucao.passo' || !evento.passo?.caminho) return;
      const passos = [...(atual.passos || [])];
      const onde = passos.findIndex(
        passo => passo.caminho === evento.passo.caminho
      );
      const passo = { ...(onde >= 0 ? passos[onde] : {}), ...evento.passo };
      if (onde >= 0) passos[onde] = passo;
      else passos.push(passo);
      atual = { ...atual, passos };
      if (passo.terminado_em) reler = true;
    });
  return { detalhe: atual, reler };
}
